import { Suspense, useRef, type FC } from 'react';
import { useQueryLoader } from 'react-relay';

import { useClientState } from 'dibs-react-hooks/exports/useClientState.ts';
import { dibsLazyClient } from 'dibs-lazy/exports/dibsLazyClient.ts';
import { getConsentedBuyerId, getGuestId } from 'dibs-cookie-jar';
import VisibilityTracker from 'dibs-visibility-tracker/exports/VisibilityTracker.tsx';
import { useHomePageContext } from '../HomePageContext.tsx';
import { HpSharedFeaturedItemsCarouselLoading } from './HpSharedFeaturedItemsCarouselLoading.tsx';
import { hpSharedFeaturedItemsQuery } from './HpSharedFeaturedItemsQuery.ts';

import { trackHpSponsoredListingsModuleTest } from '../helpers/abTestHelpers.ts';

import { type HpSharedFeaturedItemsQuery } from './__generated__/HpSharedFeaturedItemsQuery.graphql.ts';

const HpSharedFeaturedItemsCarousel = dibsLazyClient(
    () =>
        import(
            /* webpackChunkName: "HpSharedFeaturedItemsCarousel" */ './HpSharedFeaturedItemsCarousel.tsx'
        )
);

type HpSharedFeaturedItemsProps = {
    userId: string | null;
    isTrade: boolean;
};

export const HpSharedFeaturedItems: FC<HpSharedFeaturedItemsProps> = ({ userId, isTrade }) => {
    const [queryRef, loadQuery] = useQueryLoader<HpSharedFeaturedItemsQuery>(
        hpSharedFeaturedItemsQuery
    );

    const featuredItemsRef = useRef(null);
    const { isHpSponsoredListingsModuleVariant, isMobile, priceBookName } = useHomePageContext();
    const isClient = useClientState();

    if (!isClient) {
        return null;
    }

    const loading = <HpSharedFeaturedItemsCarouselLoading isMobile={isMobile} />;

    return (
        <div ref={featuredItemsRef}>
            <VisibilityTracker
                elementRef={featuredItemsRef}
                onVisibilityChange={({ isVisible }) => {
                    if (isVisible) {
                        trackHpSponsoredListingsModuleTest();
                    }
                }}
            />
            {isHpSponsoredListingsModuleVariant && (
                <>
                    <VisibilityTracker
                        elementRef={featuredItemsRef}
                        onVisibilityChange={({ isVisible }) => {
                            if (isVisible) {
                                const personalizationId = getConsentedBuyerId(document.cookie);
                                const guestId = getGuestId(document.cookie);
                                loadQuery({
                                    userId: userId || '',
                                    hasUserId: !!userId,
                                    personalizationId,
                                    guestId,
                                    isTrade,
                                    priceBookName,
                                    minSponsoredItemsSteps: isMobile ? [4] : [5, 10],
                                });
                            }
                        }}
                        observerOptions={{
                            rootMargin: '100% 0px',
                        }}
                    />
                    {queryRef ? (
                        <Suspense fallback={loading}>
                            <HpSharedFeaturedItemsCarousel queryRef={queryRef} />
                        </Suspense>
                    ) : (
                        loading
                    )}
                </>
            )}
        </div>
    );
};
